import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import projects from "./projects";
import { getSkillColor } from "../../utils/getUniqueSkills";
import { getProjectFilename } from "../terminal/fileSystem";

const ProjectsSection = ({ hoveredSkill, onCollapseOthersChange, onTerminalCommand }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [expandedTech, setExpandedTech] = useState({});
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const displayedProjects = showAll ? projects : projects.slice(0, 2);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  const toggleTech = (index) => {
    setExpandedTech((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const cardMatchesSkill = (project) => {
    if (!hoveredSkill) return true;
    return project.tech.split(", ").map((t) => t.trim()).includes(hoveredSkill);
  };

  useEffect(() => {
    if (onCollapseOthersChange) {
      onCollapseOthersChange(showAll && !collapsed);
    }
  }, [showAll, collapsed, onCollapseOthersChange]);

  return (
    <section
      className="py-4 border-b"
      style={{ borderColor: "var(--border-color)" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="flex items-center gap-2 text-left"
          >
            <h2
              className="text-xl font-bold"
              style={{ color: "var(--text-primary)" }}
            >
              Projects
            </h2>
            <span
              className="text-xs transition-transform duration-200"
              style={{
                color: "var(--text-tertiary)",
                transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
              }}
            >
              ▼
            </span>
          </button>

          {!collapsed && !showAll && projects.length > 2 && (
            <button
              onClick={() => setShowAll(true)}
              className="text-xs hover:opacity-70 transition-opacity"
              style={{ color: "var(--accent)" }}
            >
              View all projects ({projects.length}) ↓
            </button>
          )}
          {!collapsed && showAll && (
            <button
              onClick={() => setShowAll(false)}
              className="text-xs hover:opacity-70 transition-opacity"
              style={{ color: "var(--accent)" }}
            >
              Show less ↑
            </button>
          )}
        </div>

        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
        <div className="space-y-2 mb-3">
          {displayedProjects.map((project, index) => (
            <div
              key={index}
              className="p-3 rounded-lg transition-all duration-300 relative overflow-hidden cursor-pointer"
              style={{
                backgroundColor:
                  hoveredCard === index
                    ? "var(--bg-card-hover)"
                    : "var(--bg-card)",
                border: hoveredSkill && cardMatchesSkill(project)
                  ? "1px solid rgba(200, 210, 230, 0.3)"
                  : "1px solid rgba(255, 255, 255, 0.05)",
                opacity: cardMatchesSkill(project) ? 1 : 0.25,
                transition: "opacity 0.3s ease, border-color 0.3s ease",
              }}
              onClick={() => onTerminalCommand?.(`cat projects/${getProjectFilename(project.name)}`)}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
              onMouseMove={handleMouseMove}
            >
              {/* Gloss effect */}
              {hoveredCard === index && (
                <div
                  className="absolute inset-0 pointer-events-none transition-opacity duration-300"
                  style={{
                    background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255,255,255,0.05) 0%, transparent 50%)`,
                    opacity: 0.5,
                  }}
                />
              )}

              <div className="relative z-10">
                <div className="flex items-start justify-between mb-1">
                  <h3
                    className="text-base font-medium flex-1"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {project.name}
                  </h3>

                  {/* Minimal Icon Links */}
                  {(project.link || project.github) && (
                    <div className="flex gap-2 ml-3">
                      {project.link && (
                        <a
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:opacity-70 transition-opacity"
                          style={{ color: "var(--accent)" }}
                          title="Live Demo"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                        </a>
                      )}
                      {project.github && (
                        <a
                          href={project.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:opacity-70 transition-opacity"
                          style={{ color: "var(--accent)" }}
                          title="GitHub"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                          </svg>
                        </a>
                      )}
                    </div>
                  )}
                </div>

                <p
                  className="text-xs mb-2"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  {project.date}
                </p>

                {/* Expandable Tech Stack */}
                <button
                  onClick={(e) => { e.stopPropagation(); toggleTech(index); }}
                  className="text-xs hover:opacity-70 transition-opacity"
                  style={{ color: "var(--accent)" }}
                >
                  {expandedTech[index]
                    ? "Hide tech stack ↑"
                    : "Show tech stack ↓"}
                </button>

                <AnimatePresence>
                  {(expandedTech[index] || (hoveredSkill && cardMatchesSkill(project))) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {project.tech.split(", ").map((tech, i) => (
                          <span
                            key={i}
                            className="px-1.5 py-px rounded text-xs"
                            style={{
                              backgroundColor: tech === hoveredSkill
                                ? "rgba(255, 255, 255, 0.08)"
                                : "rgba(255, 255, 255, 0.03)",
                              color: getSkillColor(tech),
                              opacity: hoveredSkill && tech !== hoveredSkill ? 0.5 : 1,
                            }}
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          ))}
        </div>

            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};

export default ProjectsSection;
